import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Clock, AlertTriangle, Wrench, CheckCircle, Truck, Factory, TrendingUp, ArrowRight } from 'lucide-react';
import api from '../api/api';

const KPICard = ({ title, value, subtitle, icon: Icon, color, onClick, testId }) => (
  <div
    className={`bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-all cursor-pointer kpi-animate ${onClick ? 'hover:border-[#16a34a]' : ''}`}
    onClick={onClick}
    data-testid={testId}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
        <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Barlow, sans-serif', color }}>{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className="p-3 rounded-xl" style={{ backgroundColor: color + '15' }}>
        <Icon size={22} style={{ color }} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/reports/kpi'),
      api.get('/alerts')
    ]).then(([kpiRes, alertsRes]) => {
      setKpis(kpiRes.data);
      setAlerts(alertsRes.data.slice(0, 5));
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]"></div>
      </div>
    );
  }

  const severityColor = { critical: '#ef4444', warning: '#eab308', info: '#3b82f6' };
  const severityLabel = { critical: 'Crítica', warning: 'Aviso', info: 'Info' };

  return (
    <div className="p-6 md:p-8 page-enter">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">Ecoparque de Cáceres · Resumen operativo</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Equipos Operativos"
          value={kpis?.operative ?? 0}
          subtitle={`de ${kpis?.total_equipment ?? 0} totales`}
          icon={CheckCircle}
          color="#16a34a"
          onClick={() => navigate('/maquinaria-movil')}
          testId="kpi-operative"
        />
        <KPICard
          title="Alertas Activas"
          value={kpis?.alert_count ?? 0}
          subtitle="Requieren atención"
          icon={AlertTriangle}
          color="#eab308"
          onClick={() => navigate('/alertas')}
          testId="kpi-alerts"
        />
        <KPICard
          title="Horas Este Mes"
          value={kpis?.hours_this_month?.toLocaleString('es-ES') ?? 0}
          subtitle="Total flota"
          icon={Clock}
          color="#3b82f6"
          testId="kpi-hours-month"
        />
        <KPICard
          title="OTs Pendientes"
          value={kpis?.pending_wo ?? 0}
          subtitle={`${kpis?.completed_wo ?? 0} completadas`}
          icon={Wrench}
          color="#8b5cf6"
          onClick={() => navigate('/ordenes-trabajo')}
          testId="kpi-pending-wo"
        />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Monthly trend chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Horas de Operación - Últimos 12 meses</h3>
              <p className="text-xs text-gray-500">Total flota Ecoparque</p>
            </div>
            <TrendingUp size={16} className="text-[#16a34a]" />
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={kpis?.monthly_trend || []} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                formatter={(v) => [`${v} h`, 'Horas']}
              />
              <Bar dataKey="hours" fill="#16a34a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Quick stats */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Estado de la Flota</h3>
          {[
            { label: 'Operativos', value: kpis?.operative, total: kpis?.total_equipment, color: '#16a34a' },
            { label: 'Con Avería', value: kpis?.breakdown, total: kpis?.total_equipment, color: '#ef4444' },
            { label: 'Horas Año', value: kpis?.hours_this_year?.toLocaleString('es-ES'), color: '#3b82f6', noBar: true },
          ].map((item) => (
            <div key={item.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">{item.label}</span>
                <span className="font-semibold" style={{ color: item.color }}>{item.value}</span>
              </div>
              {!item.noBar && (
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${((item.value || 0) / (item.total || 1)) * 100}%`, backgroundColor: item.color }}
                  />
                </div>
              )}
            </div>
          ))}

          <div className="mt-auto pt-4 border-t border-gray-100 grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Truck size={18} className="text-[#16a34a] mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>22</p>
              <p className="text-[10px] text-gray-500">Maq. Móvil</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Factory size={18} className="text-blue-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>21</p>
              <p className="text-[10px] text-gray-500">Maq. Fija</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top machines by hours */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Top Equipos por Horas Totales</h3>
            <button onClick={() => navigate('/maquinaria-movil')} className="text-xs text-[#16a34a] hover:underline flex items-center gap-1">
              Ver todos <ArrowRight size={12} />
            </button>
          </div>
          <div className="space-y-3">
            {(kpis?.top_machines || []).map((m, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-4 font-mono">{i + 1}</span>
                <div className="flex-1">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium truncate">{m.name}</span>
                    <span className="text-gray-500 font-mono ml-2">{m.hours?.toLocaleString('es-ES')} h</span>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#16a34a] rounded-full"
                      style={{ width: `${((m.hours || 0) / (kpis?.top_machines?.[0]?.hours || 1)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Alerts */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Alertas Recientes</h3>
            <button onClick={() => navigate('/alertas')} className="text-xs text-[#16a34a] hover:underline flex items-center gap-1">
              Ver todas <ArrowRight size={12} />
            </button>
          </div>
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <CheckCircle size={32} className="mb-2 text-green-400" />
              <p className="text-sm">Sin alertas activas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                  data-testid={`alert-item-${alert.id}`}
                >
                  <div
                    className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                    style={{ backgroundColor: severityColor[alert.severity] || '#6b7280' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{alert.title}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{alert.equipment_name || alert.equipment_code}</p>
                  </div>
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{
                      backgroundColor: (severityColor[alert.severity] || '#6b7280') + '20',
                      color: severityColor[alert.severity] || '#6b7280'
                    }}
                  >
                    {severityLabel[alert.severity] || alert.severity}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
